/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/buyBoxNotifications.scss';

import React, {MouseEventHandler, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {BuyBoxEvent, NotificationType} from '../../../util/enums';

import Notification from '../../../models/notificationModel';

import {AwesomeIcon, Checkbox, CloseButton, hasHover, Link, voidFunction} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import {NotificationProps, NotificationTypeProps} from '../notificationsPage';

import useDefaultNotification from '../useDefaultNotification';

import NotificationEmail from '../notificationComponents/notificationEmail';

import NotificationProducts from '../notificationComponents/notificationProducts';

import NotificationActions from '../notificationComponents/notificationActions';

import BolmateBadgePremium from '../bolmateBadgePremium';

function BuyBoxNotification(props: NotificationProps) {
    const {t} = useTranslation();
    const {
        notification,
        loading,
        onChangeNotification,
        onDeleteNotification,
        onSendTestNotification,
        disabled,
        canEdit,
        canDelete,
        onReplaceNotification
    } = props;
    const [eventsOpen, setEventsOpen] = useState(false);
    const eventsRef = useRef<HTMLDivElement>(null);
    const [onChangeEmailAddresses] = useDefaultNotification({...props});

    function onDocumentClick() {
        if (!hasHover(eventsRef.current)) onCloseEvents();
    }

    function onOpenEvents() {
        setEventsOpen(!eventsOpen);
    }

    function onSelectEvent(value: BuyBoxEvent) {
        return function () {
            const notificationSettings = notification.buyBoxConfig;
            if (notificationSettings.buy_box_events.has(value)) notificationSettings.buy_box_events.delete(value);
            else notificationSettings.buy_box_events.add(value);
            notification.flex = notificationSettings.dump();
            onChangeNotification(notification);
        };
    }

    function onCloseEvents() {
        setEventsOpen(false);
        const config = notification.buyBoxConfig;
        if (!config.buy_box_events.size) {
            // Wait 200ms to allow the options to disappear
            window.setTimeout(() => {
                config.buy_box_events = new Set([BuyBoxEvent.GOT, BuyBoxEvent.LOST]);
                notification.flex = config.dump();
                onChangeNotification(notification);
            }, 200);
        }
    }

    function renderEvents() {
        let label: string;
        const selected = notification.buyBoxConfig.buy_box_events;
        if (!selected.size) label = t('notifications.buyBox.eventChange', 'Koopblok verandering');
        else if (selected.size === 1) {
            const event = Array.from(selected)[0];
            if (event === BuyBoxEvent.LOST) label = t('notifications.buyBox.eventLost', 'Koopblok verloren');
            else if (event === BuyBoxEvent.GOT) label = t('notifications.buyBox.eventGot', 'Koopblok verkregen');
            else label = t('notifications.buyBox.eventChange', 'Koopblok verandering');
        } else label = t('notifications.buyBox.eventChange', 'Koopblok verandering');
        return (
            <section className='events-container' ref={eventsRef}>
                <label className='fixed-text'>{t('notifications.buyBox.atLabel', 'Bij')}</label>
                {renderButton(label, onOpenEvents, 'events-button')}
                <div className={classNames('events-select-container', eventsOpen && !disabled && 'open')}>
                    <CloseButton onClick={onCloseEvents} className='close-events' />
                    {renderEvent(BuyBoxEvent.LOST)}
                    {renderEvent(BuyBoxEvent.GOT)}
                </div>
            </section>
        );
    }

    function renderEvent(event: BuyBoxEvent) {
        const selected = notification.buyBoxConfig.buy_box_events.has(event);
        let label: string;
        if (event === BuyBoxEvent.GOT) label = t('notifications.buyBox.eventGot', 'Koopblok verkregen');
        else if (event === BuyBoxEvent.LOST) label = t('notifications.buyBox.eventLost', 'Koopblok verloren');
        else label = t('notifications.buyBox.eventChanged', 'Koopblok gewijzigd');
        return (
            <div className='event-container' onClick={onSelectEvent(event)}>
                <Checkbox checked={selected} onCheck={voidFunction} label={label} />
            </div>
        );
    }

    function renderButton(label: string, onClick: MouseEventHandler, className?: string) {
        const _disabled = loading || disabled || !canEdit;
        return (
            <div
                className={classNames('var-button', _disabled && 'disabled', className)}
                onClick={_disabled ? undefined : onClick}
            >
                <label className='button-label'>{label}</label>
            </div>
        );
    }

    function renderTitle() {
        return (
            <div className='notification-title beta'>
                <AwesomeIcon name='trophy' className='type-icon' />{' '}
                {t('notifications.buyBox.title', 'Koopblok notificatie')} <BolmateBadgePremium /> {renderTip()}
            </div>
        );
    }

    function renderTip() {
        return (
            <span className='notification-info-label info'>
                <AwesomeIcon name='lightbulb' /> {t('notifications.buyBox.tipLabel', 'Tip')}{' '}
                <p className='beta-info'>
                    {t('notifications.buyBox.tipLine1', 'Krijg je veel koopblok-notificaties?')}
                    <br />
                    {t(
                        'notifications.buyBox.tipLine2',
                        'Dat komt vaak door hoge concurrentie, automatische repricers en veranderende leverbeloftes in het weekend.'
                    )}
                    <br />
                    {t(
                        'notifications.buyBox.tipLine3',
                        'Wil je dat deze meldingen niet meer in je algemene inbox terechtkomen?'
                    )}
                    <br />
                    <Link
                        route='https://support.bolmate.nl/article/54-ik-krijg-erg-veel-bol-notificaties-binnen-wat-moet-ik-doen'
                        target='_blank'
                    >
                        {t(
                            'notifications.buyBox.tipLinkText',
                            '👉 Lees hier hoe je ze automatisch in een aparte map ontvangt in Gmail of Outlook.'
                        )}
                    </Link>
                </p>
            </span>
        );
    }

    useEffect(() => {
        if (eventsOpen)
            window.setTimeout(() => {
                document.addEventListener('click', onDocumentClick);
            });
        else document.removeEventListener('click', onDocumentClick);

        return function () {
            document.removeEventListener('click', onDocumentClick);
        };
    }, [eventsOpen]);

    return (
        <div className={classNames('buy-box-notification notification-config', disabled && 'disabled')}>
            {renderTitle()}
            <div className='notification'>
                {renderEvents()}
                <NotificationProducts
                    onSuccess={onReplaceNotification}
                    notification={notification}
                    disabled={loading || disabled || !canEdit}
                />
                <NotificationEmail
                    onChangeEmailAddresses={onChangeEmailAddresses}
                    emailAddresses={notification.emailAddresses}
                    disabled={loading || disabled || !canEdit}
                />
            </div>
            <NotificationActions
                onDeleteNotification={onDeleteNotification}
                onSendTestNotification={onSendTestNotification}
                disabled={disabled}
                notification={notification}
                canDelete={canDelete}
            />
        </div>
    );
}

export default function BuyBoxNotifications(props: NotificationTypeProps) {
    const {notifications, moduleActive} = props;
    const buyBoxNotifications = useMemo(() => {
        return notifications.filter(
            (notification: Notification) => notification.notificationType === NotificationType.BUY_BOX
        );
    }, [notifications]);
    if (!buyBoxNotifications.length) return null;

    function renderNotification(notification: Notification, index: number) {
        return <BuyBoxNotification key={index} notification={notification} disabled={!moduleActive} {...props} />;
    }

    return (
        <div className='notification-type buy-box-notifications'>
            <div className='notifications'>{buyBoxNotifications.map(renderNotification)}</div>
        </div>
    );
}
